import { isDefined } from '@aloft/toolkit-shared';

import { Feature, FeatureCollection, Point } from 'geojson';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';

import { StatusFilterControl } from './StatusFilterControl.tsx';
import {
  MAP_LAYERS,
  MAP_LAYER_DEFINITIONS,
  MAP_PIN_STATUS_FILTERS,
  MAP_SOURCES,
} from './constants.ts';
import { Pin, PinStatusFilter, UseMapProps } from './types.ts';

const getStatusIcon = (status: PinStatusFilter) => {
  const key = status.toLocaleUpperCase() as keyof typeof MAP_PIN_STATUS_FILTERS;
  return key in MAP_PIN_STATUS_FILTERS ? key.toLocaleLowerCase() : 'expired';
};

const getPinStateSuffix = (pin: Pin) => {
  if (pin.active) return '-selected';
  if (pin.selected) return '-highlight';
  if (pin.hovered) return '-hover';
  return '';
};

const getPinIdFromFeature = (
  feature: mapboxgl.MapboxGeoJSONFeature | undefined,
): string | undefined => {
  const id = feature?.properties?.id;
  return id === undefined || id === null ? undefined : String(id);
};

const addPinLayers = (map: mapboxgl.Map) => {
  if (!map.getLayer(MAP_LAYERS.PINS_HOVER)) {
    map.addLayer(MAP_LAYER_DEFINITIONS.PINS_HOVER);
  }
  if (!map.getLayer(MAP_LAYERS.PINS)) {
    map.addLayer(MAP_LAYER_DEFINITIONS.PINS);
  }
  if (!map.getLayer(MAP_LAYERS.PINS_HIT)) {
    map.addLayer(MAP_LAYER_DEFINITIONS.PINS_HIT);
  }
  if (map.getLayer(MAP_LAYER_DEFINITIONS.SUBJECT.id)) {
    map.moveLayer(MAP_LAYER_DEFINITIONS.SUBJECT.id);
  }
};

const setPinLayerFilters = (
  map: mapboxgl.Map,
  enableStatusFiltering: boolean,
  selectedStatuses?: PinStatusFilter[],
) => {
  const pinLayers = [MAP_LAYERS.PINS_HOVER, MAP_LAYERS.PINS, MAP_LAYERS.PINS_HIT];
  if (!enableStatusFiltering || !selectedStatuses) {
    pinLayers.forEach((layerId) => map.setFilter(layerId, null));
    return;
  }

  const filter = ['in', ['get', 'status'], ['literal', selectedStatuses]];
  pinLayers.forEach((layerId) => map.setFilter(layerId, filter));
};

export const propertyPinsToGeoJSON = (
  pins: Pin[],
): {
  type: 'geojson';
  data: FeatureCollection<Point>;
} => {
  const features: Feature<Point>[] = pins.map((pin) => {
    let iconName = getStatusIcon(pin.icon);

    if (pin.valuationType === 'arv') {
      iconName += '-arv';
    } else if (pin.valuationType === 'aiv') {
      iconName += '-aiv';
    } else {
      iconName += getPinStateSuffix(pin);
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [pin.lng, pin.lat],
      },
      properties: {
        id: pin.id,
        icon: iconName,
        label: pin.label,
        status: getStatusIcon(pin.status),
        selected: pin.selected,
        active: pin.active,
        hovered: pin.hovered ?? false,
        valuationType: pin.valuationType,
      },
    };
  });

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features,
    },
  };
};

type Props<Feature> = {
  mapRef: UseMapProps<Feature>['mapRef'];
  pins: UseMapProps<Feature>['pins'];
  selectedStatuses?: UseMapProps<Feature>['selectedStatuses'];
  enableStatusFiltering?: UseMapProps<Feature>['enableStatusFiltering'];
  statusFilterVariant?: UseMapProps<Feature>['statusFilterVariant'];
  callbacks: {
    onPinFilterChange?: NonNullable<UseMapProps<Feature>['callbacks']>['onPinFilterChange'];
    onMapLoad: NonNullable<UseMapProps<Feature>['callbacks']>['onMapLoad'];
    onPinSourceLoad: NonNullable<UseMapProps<Feature>['callbacks']>['onPinSourceLoad'];
    onPinClick: NonNullable<UseMapProps<Feature>['callbacks']>['onPinClick'];
    onPinHoverChange?: NonNullable<UseMapProps<Feature>['callbacks']>['onPinHoverChange'];
    onMoveEnd: NonNullable<UseMapProps<Feature>['callbacks']>['onMoveEnd'];
  };
};

export const usePinLayerCallbacks = <Feature>({
  mapRef,
  pins,
  selectedStatuses,
  enableStatusFiltering = false,
  statusFilterVariant = 'sales',
  callbacks,
}: Props<Feature>) => {
  const statusControlRef = useRef<StatusFilterControl | null>(null);

  const addStatusFilterControl = (map: mapboxgl.Map) => {
    if (!statusControlRef.current) {
      const statusControl = new StatusFilterControl({
        selected: selectedStatuses || [],
        onChange: callbacks.onPinFilterChange || (() => {}),
        variant: statusFilterVariant,
      });

      statusControlRef.current = statusControl;
      map.addControl(statusControl, 'bottom-left');
    } else {
      statusControlRef.current.updateProps({
        selected: selectedStatuses || [],
        onChange: callbacks.onPinFilterChange || (() => {}),
        variant: statusFilterVariant,
      });
    }
  };

  const handleOnLoadSource = (e: mapboxgl.MapboxEvent<undefined> & mapboxgl.EventData) => {
    const map = e.target;
    if (!isDefined(map)) return;
    callbacks.onMapLoad?.();

    if (!isDefined(pins)) return;
    if (!map.getSource(MAP_SOURCES.PINS)) {
      map.addSource(MAP_SOURCES.PINS, propertyPinsToGeoJSON(pins));
    }

    if (!map.getLayer(MAP_LAYERS.PINS)) {
      addPinLayers(map);
    }

    if (enableStatusFiltering) addStatusFilterControl(map);
  };

  const handleSourcedata = (e: mapboxgl.MapSourceDataEvent & mapboxgl.EventData) => {
    const { sourceId, isSourceLoaded, target } = e;
    if (sourceId === MAP_SOURCES.PINS && isSourceLoaded) {
      callbacks.onPinSourceLoad?.(MAP_SOURCES.PINS, target);
      if (enableStatusFiltering) addStatusFilterControl(target);
    }
  };

  const handlePinClick = (event: mapboxgl.MapLayerMouseEvent & mapboxgl.EventData) => {
    const feature = (event.features && event.features[0]) as unknown as Feature | undefined;
    callbacks.onPinClick?.(feature);
  };

  const handlePinMouseEnter = (event: mapboxgl.MapLayerMouseEvent & mapboxgl.EventData) => {
    const map = event.target;
    map.getCanvas().style.cursor = 'pointer';
    callbacks.onPinHoverChange?.(getPinIdFromFeature(event.features?.[0]));
  };

  const handlePinMouseLeave = (event: mapboxgl.MapLayerMouseEvent & mapboxgl.EventData) => {
    const map = event.target;
    map.getCanvas().style.cursor = 'grab';
    callbacks.onPinHoverChange?.(undefined);
  };

  const handleMoveEnd = (
    e: mapboxgl.MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined> & mapboxgl.EventData,
  ) => {
    const map = e.target;
    if (!isDefined(map)) return;

    callbacks.onMoveEnd?.({
      center: map.getCenter(),
      zoom: map.getZoom(),
    });
  };

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    if (!enableStatusFiltering) {
      if (statusControlRef.current) {
        map.removeControl(statusControlRef.current);
        statusControlRef.current = null;
      }
      return;
    }

    addStatusFilterControl(map);
  }, [enableStatusFiltering, selectedStatuses, statusFilterVariant, callbacks.onPinFilterChange]);

  // Update map source when pins change
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!isDefined(map) || !map.isStyleLoaded() || !isDefined(pins)) return;

    const geoJson = propertyPinsToGeoJSON(pins);
    const source = map.getSource(MAP_SOURCES.PINS);
    if (source && source.type === 'geojson') {
      source.setData(geoJson.data);
    } else {
      map.addSource(MAP_SOURCES.PINS, geoJson);
    }

    addPinLayers(map);
    setPinLayerFilters(map, enableStatusFiltering, selectedStatuses);
  }, [mapRef, pins, enableStatusFiltering, selectedStatuses]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded() || !map.getLayer(MAP_LAYERS.PINS)) {
      return;
    }

    setPinLayerFilters(map, enableStatusFiltering, selectedStatuses);
  }, [mapRef, selectedStatuses, statusFilterVariant, enableStatusFiltering]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!isDefined(map)) return;

    map.on('load', handleOnLoadSource);
    map.on('sourcedata', handleSourcedata);

    map.on('click', MAP_LAYERS.PINS_HIT, handlePinClick);
    map.on('mouseenter', MAP_LAYERS.PINS_HIT, handlePinMouseEnter);
    map.on('mouseleave', MAP_LAYERS.PINS_HIT, handlePinMouseLeave);
    map.on('moveend', handleMoveEnd);

    return () => {
      map.off('load', handleOnLoadSource);
      map.off('sourcedata', handleSourcedata);
      map.off('click', MAP_LAYERS.PINS_HIT, handlePinClick);
      map.off('mouseenter', MAP_LAYERS.PINS_HIT, handlePinMouseEnter);
      map.off('mouseleave', MAP_LAYERS.PINS_HIT, handlePinMouseLeave);
      map.off('moveend', handleMoveEnd);
    };
  }, [
    callbacks,
    mapRef,
    selectedStatuses,
    statusControlRef,
    statusFilterVariant,
    enableStatusFiltering,
  ]);
};
